import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Camera, Upload, X, Loader2, RotateCw, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ENHANCE_STYLES = [
  { id: 'professional', label: 'Professional', emoji: '💼', desc: 'Clean studio headshot' },
  { id: 'casual',       label: 'Casual',        emoji: '☀️', desc: 'Warm golden-hour glow' },
  { id: 'bw',           label: 'B & W',         emoji: '🎞️', desc: 'High-contrast editorial' },
  { id: 'cinematic',    label: 'Cinematic',     emoji: '🎬', desc: 'Magazine-style portrait' },
];

// Reusable circular avatar with two ways to set a photo:
//  • "Take Photo"  — opens an in-app webcam capture modal (uses getUserMedia).
//                    On mobile this prompts for camera permission and shows
//                    the live stream; falls back to the device camera roll
//                    via <input capture> when getUserMedia is unavailable.
//  • "Upload"      — regular file picker.
// Photo is uploaded via /api/admin/upload and the absolute URL is passed
// back via onChange. Initials are shown when no photo is set.
//
// Props:
//   value        — current photo URL (or empty string)
//   onChange(url) — called with the new absolute URL after successful upload
//   token        — admin auth token for /api/admin/upload
//   size         — diameter of the circular avatar in px (default 96)
//   accent       — accent color for border + initials (default Hyatt blue)
//   initials     — fallback initials when no photo (e.g. "AM")
//   testIdPrefix — namespace for data-testid attrs (default "photo-capture")
//   prominent    — when true, swaps the secondary "Take Photo" pill for a
//                  bold primary "Take Face Shot" CTA + always renders the
//                  "Enhance with AI" affordance (disabled until a photo is
//                  set). Used by EditUserDialog in create mode.
//   signupMode   — when true, posts to the PUBLIC /api/auth/upload-signup-photo
//                  endpoint (no auth token needed) and HIDES the AI Enhance
//                  button (which requires admin auth). Used by AuthGatePage
//                  to capture a face shot during sign-up before the user
//                  has any credentials.
export default function PhotoCapture({
  value,
  onChange,
  token,
  size = 96,
  accent = '#1F4FBE',
  initials = '?',
  testIdPrefix = 'photo-capture',
  prominent = false,
  signupMode = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const fileInputRef = useRef(null);
  const fallbackCameraInputRef = useRef(null);

  const uploadBlob = useCallback(async (blob, filename = 'photo.jpg') => {
    if (!blob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, filename);
      // Sign-up runs BEFORE the user has any credentials, so we hit a
      // dedicated public endpoint that's image-only and size-capped.
      const url = signupMode ? `${API}/auth/upload-signup-photo` : `${API}/admin/upload`;
      const headers = signupMode
        ? { 'Content-Type': 'multipart/form-data' }
        : { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' };
      const { data } = await axios.post(url, fd, {
        headers,
        withCredentials: !signupMode,
      });
      const finalUrl = data?.url?.startsWith('http') ? data.url : `${BACKEND}${data.url}`;
      onChange(finalUrl);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  }, [onChange, token, signupMode]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadBlob(f, f.name);
    e.target.value = '';
  };

  // True webcam capture supported?
  const supportsGetUserMedia = typeof navigator !== 'undefined'
    && navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';

  const openCamera = () => {
    if (supportsGetUserMedia) {
      setCameraOpen(true);
    } else {
      // Mobile fallback — opens native camera via <input capture>
      fallbackCameraInputRef.current?.click();
    }
  };

  return (
    <div className="flex flex-col items-center gap-2" data-testid={`${testIdPrefix}-root`}>
      {/* Circular avatar preview */}
      <div
        className="relative rounded-full overflow-hidden border-2 flex items-center justify-center flex-shrink-0"
        style={{ width: size, height: size, borderColor: accent, backgroundColor: `${accent}12` }}
      >
        {uploading ? (
          <Loader2 className="animate-spin" style={{ color: accent, width: size * 0.4, height: size * 0.4 }} />
        ) : value ? (
          <img src={value} alt="Profile" className="w-full h-full object-cover" data-testid={`${testIdPrefix}-img`} />
        ) : (
          <span className="font-bold" style={{ color: accent, fontFamily: 'Manrope', fontSize: size * 0.32 }}>
            {initials}
          </span>
        )}
        {value && !uploading && (
          <button
            type="button"
            onClick={() => onChange('')}
            data-testid={`${testIdPrefix}-clear`}
            title="Remove photo"
            className="absolute top-0 right-0 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', transform: 'translate(25%, -25%)' }}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Action buttons. In `prominent` mode (used for create-user) the
          camera CTA becomes a full-width bold primary action and the
          "Enhance with AI" button is always visible (disabled until a
          photo is captured) so admins discover the affordance up-front. */}
      <div className={prominent
        ? 'flex flex-col gap-2 w-full items-stretch'
        : 'flex flex-wrap justify-center gap-1.5'}>
        <button
          type="button"
          onClick={openCamera}
          disabled={uploading}
          data-testid={`${testIdPrefix}-camera-btn`}
          className={prominent
            ? 'flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold disabled:opacity-50 transition-all hover:-translate-y-0.5'
            : 'flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold disabled:opacity-50'}
          style={{
            backgroundColor: accent, color: 'white', fontFamily: 'Manrope',
            boxShadow: prominent ? `0 4px 14px ${accent}55` : 'none',
          }}
        >
          <Camera className={prominent ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
          {prominent ? (value ? 'Retake face shot' : 'Take face shot') : 'Take Photo'}
        </button>
        <div className={prominent ? 'flex gap-2' : 'contents'}>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-upload-btn`}
            className={prominent
              ? 'flex-1 flex items-center justify-center gap-1 px-2.5 py-2 rounded-md text-[11px] font-bold disabled:opacity-50'
              : 'flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold disabled:opacity-50'}
            style={{ backgroundColor: '#1A2E2E', color: '#A3D4D4', fontFamily: 'Manrope' }}
          >
            <Upload className="w-3.5 h-3.5" /> Upload
          </button>
          {(value || prominent) && (
            <button
              type="button"
              onClick={() => value && !uploading && setEnhanceOpen(true)}
              disabled={!value || uploading}
              data-testid={`${testIdPrefix}-enhance-btn`}
              title={value ? 'Polish this photo with AI' : 'Take a photo first to enable AI polish'}
              className={
                (prominent ? 'flex-1 ' : '') +
                'flex items-center justify-center gap-1 px-2.5 py-2 rounded-md text-[11px] font-bold transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:hover:translate-y-0'
              }
              style={{
                background: value
                  ? 'linear-gradient(135deg, #B8860B 0%, #F4E4BC 50%, #D4AF37 100%)'
                  : 'linear-gradient(135deg, #B8860B33 0%, #F4E4BC33 50%, #D4AF3733 100%)',
                color: value ? '#0A1A2F' : '#7A8C8C',
                fontFamily: 'Manrope',
                boxShadow: value ? '0 2px 8px rgba(184, 134, 11, 0.35)' : 'none',
                border: value ? 'none' : '1px dashed #B8860B66',
              }}
            >
              <Sparkles className="w-3.5 h-3.5" /> Enhance with AI
            </button>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        data-testid={`${testIdPrefix}-file-input`}
      />
      <input
        ref={fallbackCameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={handleFileChange}
        data-testid={`${testIdPrefix}-fallback-camera-input`}
      />

      {cameraOpen && (
        <CameraModal
          onCancel={() => setCameraOpen(false)}
          onCapture={(blob) => { setCameraOpen(false); uploadBlob(blob); }}
          accent={accent}
        />
      )}

      {enhanceOpen && (
        <EnhanceModal
          originalUrl={value}
          token={token}
          signupMode={signupMode}
          onCancel={() => setEnhanceOpen(false)}
          onAccept={(url) => { setEnhanceOpen(false); onChange(url); }}
        />
      )}
    </div>
  );
}

// In-app webcam capture modal. Uses getUserMedia to show a live preview, lets
// the user flip front/back camera, and writes a JPEG blob to onCapture.
function CameraModal({ onCancel, onCapture, accent }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' = selfie, 'environment' = rear

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      // Always release the previous stream before requesting a new one.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (err) {
        setError(err?.message || 'Could not access camera. Check browser permissions.');
      }
    };
    start();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [facingMode]);

  const snap = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    // Mirror selfie shots so the captured image matches what the user saw.
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) onCapture(blob);
    }, 'image/jpeg', 0.92);
  };

  const flipCamera = () => setFacingMode((m) => (m === 'user' ? 'environment' : 'user'));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="w-[95vw] max-w-lg p-0 overflow-hidden" style={{ backgroundColor: '#0A1A2F' }} data-testid="camera-modal">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold flex items-center gap-2" style={{ color: 'white', fontFamily: 'Manrope' }}>
              <Camera className="w-4 h-4" /> Take a photo
            </h3>
            <button
              type="button"
              onClick={flipCamera}
              data-testid="camera-flip"
              title="Flip camera"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', fontFamily: 'Manrope' }}
            >
              <RotateCw className="w-3.5 h-3.5" /> Flip
            </button>
          </div>

          {error ? (
            <div className="rounded-lg p-4 text-center" style={{ backgroundColor: 'rgba(239,68,68,0.15)' }}>
              <p className="text-sm font-bold mb-1" style={{ color: '#FCA5A5', fontFamily: 'Manrope' }}>
                Camera unavailable
              </p>
              <p className="text-xs" style={{ color: '#FECACA', fontFamily: 'Manrope' }}>
                {error}
              </p>
            </div>
          ) : (
            <div className="rounded-lg overflow-hidden" style={{ aspectRatio: '4/3', backgroundColor: 'black' }}>
              <video
                ref={videoRef}
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                data-testid="camera-video"
              />
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={onCancel}
              data-testid="camera-cancel"
              className="flex-1 px-3 py-2.5 rounded-lg text-xs font-bold"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', fontFamily: 'Manrope' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={snap}
              disabled={!!error}
              data-testid="camera-snap"
              className="flex-1 px-3 py-2.5 rounded-lg text-xs font-bold disabled:opacity-50"
              style={{ backgroundColor: accent, color: 'white', fontFamily: 'Manrope' }}
            >
              <Camera className="w-3.5 h-3.5 inline mr-1.5" /> Snap photo
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// AI photo enhancement modal. Lets the user pick a style preset (or supply
// a custom prompt), sends the original to /api/admin/ai/enhance-image, then
// shows a side-by-side comparison so they can keep either Original or Enhanced.
// In signupMode (public sign-up flow) the post goes to the auth-router
// public endpoint instead — same Gemini pipeline, no auth header.
function EnhanceModal({ originalUrl, token, signupMode = false, onCancel, onAccept }) {
  const [style, setStyle] = useState('professional');
  const [customPrompt, setCustomPrompt] = useState('');
  const [enhanced, setEnhanced] = useState(null); // { url }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Convert the original (URL) into base64 once for sending to the backend.
  // Falls back gracefully if the image can't be fetched (CORS, missing).
  const fetchAsBase64 = async (url) => {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('Could not load original photo');
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result || '';
        const idx = result.indexOf(',');
        resolve(idx >= 0 ? result.slice(idx + 1) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const runEnhance = async () => {
    setLoading(true);
    setError(null);
    setEnhanced(null);
    try {
      const b64 = await fetchAsBase64(originalUrl);
      const url_ep = signupMode ? `${API}/auth/enhance-signup-photo` : `${API}/admin/ai/enhance-image`;
      const headers = signupMode ? {} : { Authorization: `Bearer ${token}` };
      const { data } = await axios.post(url_ep, {
        image_base64: b64,
        style,
        custom_prompt: customPrompt || null,
      }, {
        headers,
        withCredentials: !signupMode,
      });
      const url = data?.url?.startsWith('http') ? data.url : `${BACKEND}${data.url}`;
      setEnhanced({ url });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'AI enhancement failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="w-[95vw] max-w-3xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#0A1A2F' }} data-testid="enhance-modal">
        <div className="p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-base font-bold flex items-center gap-2" style={{ color: 'white', fontFamily: 'Manrope' }}>
              <Sparkles className="w-4 h-4" style={{ color: '#F4E4BC' }} /> Enhance profile photo with AI
            </h3>
            <button
              type="button"
              onClick={onCancel}
              data-testid="enhance-close"
              className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Style picker */}
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Manrope' }}>
            Choose a style
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {ENHANCE_STYLES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStyle(s.id)}
                disabled={loading}
                data-testid={`enhance-style-${s.id}`}
                className="rounded-lg p-2.5 text-left transition-all"
                style={{
                  backgroundColor: style === s.id ? 'rgba(244, 228, 188, 0.15)' : 'rgba(255,255,255,0.06)',
                  border: style === s.id ? '1px solid rgba(244, 228, 188, 0.65)' : '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <p className="text-sm font-bold" style={{ color: 'white', fontFamily: 'Manrope' }}>
                  <span className="mr-1">{s.emoji}</span> {s.label}
                </p>
                <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Manrope' }}>
                  {s.desc}
                </p>
              </button>
            ))}
          </div>

          {/* Optional prompt */}
          <input
            type="text"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Optional extra instruction (e.g. 'wear a navy blazer')"
            data-testid="enhance-custom-prompt"
            className="w-full px-3 py-2 rounded-lg text-xs mb-4 outline-none"
            style={{
              backgroundColor: 'rgba(255,255,255,0.06)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.15)',
              fontFamily: 'Manrope',
            }}
          />

          {/* Before / After preview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="rounded-lg overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
              <div className="px-2 py-1 text-center" style={{ backgroundColor: '#7A8C8C' }}>
                <span className="text-[10px] font-bold uppercase tracking-wider text-white" style={{ fontFamily: 'Manrope' }}>Original</span>
              </div>
              <img src={originalUrl} alt="Original" className="w-full h-64 object-cover" data-testid="enhance-original" />
            </div>
            <div className="rounded-lg overflow-hidden relative" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
              <div className="px-2 py-1 text-center" style={{ background: 'linear-gradient(90deg, #B8860B, #D4AF37, #B8860B)' }}>
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>
                  AI Enhanced
                </span>
              </div>
              <div className="w-full h-64 flex items-center justify-center">
                {loading ? (
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" style={{ color: '#F4E4BC' }} />
                    <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Manrope' }}>
                      Enhancing with Nano Banana…<br />
                      <span className="text-[9px] opacity-70">This usually takes 10–25 seconds</span>
                    </p>
                  </div>
                ) : enhanced ? (
                  <img src={enhanced.url} alt="Enhanced" className="w-full h-64 object-cover" data-testid="enhance-result" />
                ) : error ? (
                  <p className="text-[11px] px-3 text-center" style={{ color: '#FCA5A5', fontFamily: 'Manrope' }}>{error}</p>
                ) : (
                  <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Manrope' }}>
                    Click "Enhance" to generate
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-50"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', fontFamily: 'Manrope' }}
              data-testid="enhance-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={runEnhance}
              disabled={loading}
              data-testid="enhance-run"
              className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
              style={{
                background: 'linear-gradient(135deg, #B8860B 0%, #F4E4BC 50%, #D4AF37 100%)',
                color: '#0A1A2F',
                fontFamily: 'Manrope',
              }}
            >
              {loading
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enhancing…</>
                : <><Sparkles className="w-3.5 h-3.5" /> {enhanced ? 'Re-enhance' : 'Enhance'}</>}
            </button>
            {enhanced && (
              <button
                type="button"
                onClick={() => onAccept(enhanced.url)}
                data-testid="enhance-accept"
                className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5"
                style={{ backgroundColor: '#22C55E', color: 'white', fontFamily: 'Manrope' }}
              >
                <Check className="w-3.5 h-3.5" /> Use Enhanced
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
